import { getReportRequirements } from "../Constants/reportRequirements";

const TREND_PATTERNS = [
  "trend",
  "forecast",
  "seasonality",
  "variance",
  "correlation",
  "volatility",
  "sensitivity",
  "regression",
];

const isTrendReport = (reportKey = "") =>
  TREND_PATTERNS.some((pattern) => String(reportKey).toLowerCase().includes(pattern));

// Horizontal analysis compares two periods by definition; running it with a single
// column wastes a usage-limit attempt on an AI answer that can only say "not enough data".
const COMPARISON_PATTERNS = ["horizontal"];

export const isComparisonReport = (reportKey = "") =>
  COMPARISON_PATTERNS.some((pattern) => String(reportKey).toLowerCase().includes(pattern));

// period_count only multiplies balance-sheet columns; P&L/cash-flow data multiplies
// through a from/to range spanning different months (Wafeq groups the range by month).
const hasMultiMonthRange = (filters = {}) => {
  const from = String(filters.periodFrom || "").slice(0, 7);
  const to = String(filters.periodTo || "").slice(0, 7);
  return Boolean(from && to && from !== to);
};

const GATE_MESSAGES = {
  period_count: {
    ar: "هذا التقرير يقارن بين الفترات؛ اختر فترة مقارنة واحدة على الأقل قبل تشغيل التحليل الذكي.",
    en: "This report compares periods; select at least one comparison period before AI analysis can run.",
  },
  range: {
    ar: "هذا التقرير يحتاج بيانات فترتين على الأقل؛ حدد مدى فترات يغطي شهرين مختلفين (من/إلى) قبل تشغيل التحليل الذكي.",
    en: "This report needs data for at least two periods; select a from/to range spanning two different months before AI analysis can run.",
  },
  any: {
    ar: "هذا التقرير يحتاج فترتين على الأقل؛ اختر فترة مقارنة أو حدد مدى يغطي أكثر من شهر قبل تشغيل التحليل الذكي.",
    en: "This report needs at least two periods; select a comparison period or a range spanning more than one month before AI analysis can run.",
  },
};

export const needsComparisonPeriods = (reportKey) => {
  const requirements = getReportRequirements(reportKey);
  if (requirements.requiresComparisonPeriods !== undefined) {
    return requirements.requiresComparisonPeriods;
  }
  return isTrendReport(reportKey) || isComparisonReport(reportKey);
};

export const getComparisonBasis = (reportKey) => {
  const requirements = getReportRequirements(reportKey);
  if (requirements.comparisonBasis) return requirements.comparisonBasis;
  return isComparisonReport(reportKey) ? "period_count" : "any";
};

export const getAnalysisReadiness = ({ filters, language = "ar", selectedReport }) => {
  if (!selectedReport) {
    return { isReady: false, message: null, reason: "missing_report" };
  }

  if (needsComparisonPeriods(selectedReport)) {
    const basis = getComparisonBasis(selectedReport);
    const hasComparisonPeriods = Number(filters?.period_count || 0) >= 1;
    const hasRange = hasMultiMonthRange(filters);
    const satisfied =
      basis === "period_count"
        ? hasComparisonPeriods
        : basis === "range"
          ? hasRange
          : hasComparisonPeriods || hasRange;

    if (!satisfied) {
      return {
        isReady: false,
        reason: isTrendReport(selectedReport)
          ? "insufficient_periods_for_trend"
          : "insufficient_periods_for_comparison",
        message: language === "en" ? GATE_MESSAGES[basis].en : GATE_MESSAGES[basis].ar,
      };
    }
  }

  return { isReady: true, message: null, reason: null };
};
